/**
 * What only the credential detail screen needs — `renderCredDetail` and the four commands its
 * action row calls (`m08b-admin2.js:188`, `m03-state.js`, `m05-linkedin.js`).
 *
 * `reference.ts` and `credential-register.ts` already carry everything the register list and its
 * three dialogs use; the detail screen adds the audit line formatter, the session-only telemetry
 * stream the second timeline tab reads, admin publish, correction, contact-e-mail repair, atomic
 * reissue, and the PNG / print / OG / LinkedIn side of the action row. They live here rather than
 * in the shared files because no other screen reads them.
 *
 * Every command mutates a draft database — the provider hands `update()` a structural clone — and
 * is the prototype's simulation, not a backend call. What a real backend would have to do instead
 * is named at each site.
 */
import type { DemoCredential, DemoDatabase, DemoEvent, DemoFieldDefinition } from '../data/types';
import { publicAssetUrl } from './asset-url';
import {
  canTransition,
  type IssueInput,
  issueInDraft,
  newCorrelationId,
  prototypeNow,
  revokeInDraft,
} from './credential-register';
import { credentialViewState, credTitle, displayName, editableFields, fmtDate, publicUrl } from './reference';
import { canvasH, canvasW, credentialArtwork } from './render';

/** The prototype signs every administrative command with the demo operator. */
export const ACTOR = 'Türker Baş';

// ------------------------------------------------------------------- the audit line (`m08b:252`)

/**
 * `eventMeta` — ported including the quirk that decides what a timeline line says: an event whose
 * `meta` is null returns EARLY, so its `reason` and `correlationId` are not printed either. The
 * seed's revoke carries no meta, which is why its line reads «… · Türker Baş · » and not the
 * private reason — that reason is shown once, in the record card, and not repeated in the stream.
 */
export function eventMeta(e: DemoEvent): string {
  const m = e.meta;
  if (!m) return '';
  const parts: string[] = [];
  const val = (v: unknown): string => String(v);
  if (m.reasonPrivate) parts.push(`özel neden: ${val(m.reasonPrivate)}`);
  if (m.changes !== null && typeof m.changes === 'object')
    for (const [k, v] of Object.entries(m.changes as Record<string, unknown>)) {
      const pair = Array.isArray(v) ? (v as unknown[]) : [];
      parts.push(`${k}: ${val(pair[0] ?? '—')} → ${val(pair[1] ?? '—')}`);
    }
  if (m.from && m.to) parts.push(`${val(m.from)} → ${val(m.to)}`);
  if (Array.isArray(m.revision)) parts.push(`r${val(m.revision[0])} → r${val(m.revision[1])}`);
  else if (m.revision) parts.push(`r${val(m.revision)}`);
  if (e.reason) parts.push(`neden: ${e.reason}`);
  if (e.correlationId) parts.push(`corr ${e.correlationId}`);
  if (m.releaseHash) parts.push(`release ${val(m.releaseHash).slice(0, 10)}…`);
  if (m.manifestHash) parts.push(`manifest ${val(m.manifestHash).slice(0, 10)}…`);
  if (m.policy) parts.push(val(m.policy));
  if (m.error) parts.push(val(m.error));
  if (m.row) parts.push(`satır ${val(m.row)}`);
  if (m.newCode) parts.push(`yeni kod ${val(m.newCode)}`);
  if (m.supersedes) parts.push(`eski kod ${val(m.supersedes)}`);
  if (m.message) parts.push(`«${val(m.message)}»`);
  if (m.subject) parts.push(`konu: ${val(m.subject)}`);
  if (m.note) parts.push(val(m.note));
  if (m.format) parts.push(val(m.format));
  if (m.popupBlocked) parts.push('popup engellendi');
  if (m.via) parts.push(`via ${val(m.via)}`);
  if (m.templateVersionId) parts.push(val(m.templateVersionId));
  return parts.length > 0 ? ` · ${parts.join(' · ')}` : '';
}

// ------------------------------------------------------------- session telemetry (`m03b:119`)

export interface TelemetryEvent {
  id: string;
  credentialId: string | null;
  type: string;
  at: string;
  meta: Record<string, unknown> | null;
}

/**
 * The second timeline tab, and the reason it is a separate stream rather than a filter over the
 * audit log: a page view, a download, a print and a LinkedIn helper opening are USAGE, they carry
 * no legal weight, and mixing them into an append-only ledger would make the ledger unusable as
 * evidence. The reference keeps them in memory for the session only; so does this.
 */
const stream: TelemetryEvent[] = [];
const listeners = new Set<() => void>();
let version = 0;
let seq = 0;

export function subscribeTelemetry(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `useSyncExternalStore` needs a snapshot that only changes when the stream does. */
export const telemetryVersion = (): number => version;

export function recordTelemetry(
  credentialId: string | null,
  type: string,
  meta: Record<string, unknown> | null = null,
): void {
  seq += 1;
  stream.push({ id: `tlm_${seq.toString(36)}`, credentialId, type, at: prototypeNow(), meta });
  if (stream.length > 500) stream.shift();
  version += 1;
  for (const listener of listeners) listener();
}

export const telemetryFor = (credentialId: string): TelemetryEvent[] =>
  stream.filter((e) => e.credentialId === credentialId);

// ------------------------------------------------------------------ append-only audit (`m02:225`)

interface EventInput {
  type: string;
  command: string;
  at: string;
  actor?: string;
  actorType?: string;
  reason?: string | null;
  meta?: Record<string, unknown> | null;
  beforeDigest?: string | null;
  afterDigest?: string | null;
  correlationId?: string | null;
}

function appendEvent(db: DemoDatabase, c: DemoCredential, o: EventInput): void {
  seq += 1;
  const ev: DemoEvent = {
    id: `ev_${seq.toString(36)}`,
    credentialId: c.id,
    type: o.type,
    command: o.command,
    at: o.at,
    actor: o.actor ?? 'Sistem',
    actorType: o.actorType ?? 'system',
    reason: o.reason ?? null,
    beforeDigest: o.beforeDigest ?? null,
    afterDigest: o.afterDigest ?? null,
  };
  /* Left ABSENT rather than set to `{}` — the formatter above reads a missing meta the way the
     reference reads a null one, and prints neither the reason nor the correlation id. */
  if (o.meta) ev.meta = o.meta;
  if (o.correlationId) ev.correlationId = o.correlationId;
  c.events.push(ev);
  c.updatedAt = ev.at;
  db.eventLog.push(ev);
}

// -------------------------------------------------------------------------------- the commands

/**
 * `adminPublishCredential` — `m03-state.js:252`.
 *
 * Two events, as upstream: the administrative act and the publication it caused. The record is
 * only publishable out of `pending_admin_publish`; every other state is a 409 upstream and a
 * no-op here, which is what the screen's toast reports.
 *
 * API GAP: credential.admin_publish — the transition, the publication timestamp and both audit
 * rows in one transaction, refused with `invalid_lifecycle_transition` from any other state.
 */
export function adminPublishInDraft(db: DemoDatabase, code: string, actor: string): void {
  const c = db.credentials.find((x) => x.publicCode === code);
  if (c?.state !== 'pending_admin_publish' || !canTransition(c.state, 'active')) return;
  const at = prototypeNow();
  const before = c.state;
  c.state = 'active';
  c.publishedAt = at;
  appendEvent(db, c, {
    type: 'admin_published',
    command: 'adminPublishCredential',
    actor,
    actorType: 'admin',
    at,
    beforeDigest: before,
    afterDigest: c.state,
  });
  appendEvent(db, c, { type: 'published', command: 'adminPublishCredential', actor: 'Sistem', at });
}

export const canAdminPublish = (c: DemoCredential): boolean => c.state === 'pending_admin_publish';

// ------------------------------------------------------------------------ contact e-mail repair

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export type EmailOutcome = 'ok' | 'invalid_email' | 'no_change';

export function contactEmailOutcome(c: DemoCredential, email: string): EmailOutcome {
  const v = email.trim();
  if (!EMAIL_RE.test(v)) return 'invalid_email';
  if (v === c.privateContactEmail) return 'no_change';
  return 'ok';
}

/**
 * `updatePrivateContactEmail` — `m03-state.js:397`.
 *
 * The audit row records the DOMAINS either side of the change and never the addresses: the
 * contact e-mail is private contact data, it is not part of the subject's identity, and it may
 * never reach public metadata, the artwork or an audit `meta`.
 *
 * API GAP: credential.update_contact_email — server-side normalisation and validation, the same
 * domain-only audit row, and the delivery record re-pointed at the new address.
 */
export function updateContactEmailInDraft(
  db: DemoDatabase,
  code: string,
  email: string,
  actor: string,
): void {
  const c = db.credentials.find((x) => x.publicCode === code);
  if (!c || contactEmailOutcome(c, email) !== 'ok') return;
  const from = c.privateContactEmail;
  c.privateContactEmail = email.trim();
  appendEvent(db, c, {
    type: 'contact_email_updated',
    command: 'updatePrivateContactEmail',
    actor,
    actorType: 'admin',
    at: prototypeNow(),
    meta: {
      fromDomain: String(from ?? '').split('@')[1] ?? null,
      toDomain: c.privateContactEmail.split('@')[1] ?? null,
    },
  });
}

// ------------------------------------------------------------------- non-identity correction

/** `IDENTITY_KEYS` — `m03-state.js:350`. The holder field, plus both canonical holder keys. */
export const identityKeys = (c: DemoCredential): string[] => [
  ...new Set([c.release.holderField, 'recipient_name', 'organization_name']),
];

/** The fields the correction dialog offers: editable, and not one of the identity keys. */
export function correctableFields(c: DemoCredential): DemoFieldDefinition[] {
  const identity = identityKeys(c);
  return editableFields<DemoFieldDefinition>(c.release.fieldDefinitions).filter(
    (f) => !identity.includes(f.key),
  );
}

export interface CorrectionInput {
  claims: Record<string, string>;
  issueDate: string;
  expiresAt: string | null;
}

export interface CorrectionDiff {
  changes: Record<string, [string, string]>;
  /** Identity keys the caller tried to move — always refused, never applied. */
  identity: string[];
}

const claimValue = (c: DemoCredential, key: string): string => String(c.claims[key] ?? '');

/** The seed types `renderDataSnapshot` loosely; a correction has to read it back to update it. */
const renderData = (c: DemoCredential): Record<string, unknown> => {
  const snapshot = (c as { renderDataSnapshot?: unknown }).renderDataSnapshot;
  return snapshot !== null && typeof snapshot === 'object' ? (snapshot as Record<string, unknown>) : {};
};

export function correctionDiff(c: DemoCredential, input: CorrectionInput): CorrectionDiff {
  const identity = identityKeys(c).filter(
    (k) => input.claims[k] !== undefined && input.claims[k] !== claimValue(c, k),
  );
  const changes: Record<string, [string, string]> = {};
  for (const f of correctableFields(c)) {
    const next = input.claims[f.key];
    if (next === undefined || next === claimValue(c, f.key)) continue;
    changes[f.key] = [claimValue(c, f.key), next];
  }
  if (input.issueDate && input.issueDate !== c.issueDate) changes.issueDate = [c.issueDate, input.issueDate];
  if ((input.expiresAt ?? '') !== (c.expiresAt ?? ''))
    changes.expiresAt = [c.expiresAt ?? '', input.expiresAt ?? ''];
  return { changes, identity };
}

/**
 * `correctNonIdentityClaim` — `m03-state.js:351`.
 *
 * The record keeps its public code and its URL; what moves forward is the REVISION. Rule 5 and
 * PO 7.3 both hang off that: a wrong holder is never corrected, it is revoked and reissued, and a
 * correction produces revision N+1 rather than editing revision N in place.
 *
 * API GAP: credential.correct — validates each value against the release's own field definitions,
 * rebuilds the render data, produces a NEW immutable `RenderManifest` and artifact for the new
 * revision and moves `currentRevisionId` forward (409 on a stale base). The demo updates the
 * record and the render data it draws from, and leaves `renderManifestHash` pointing at the
 * manifest that was actually produced: a hash invented here would be a claim about bytes that
 * were never rendered (rule 10).
 */
export function correctInDraft(
  db: DemoDatabase,
  code: string,
  input: CorrectionInput,
  actor: string,
  reason: string,
): void {
  const c = db.credentials.find((x) => x.publicCode === code);
  if (!c || c.state === 'revoked') return;
  const { changes, identity } = correctionDiff(c, input);
  if (identity.length > 0 || Object.keys(changes).length === 0) return;

  const at = prototypeNow();
  const correlationId = newCorrelationId();
  const lang: 'tr' | 'en' = c.lang === 'en' ? 'en' : 'tr';
  const claims: Record<string, unknown> = { ...c.claims };
  for (const f of correctableFields(c)) {
    const next = input.claims[f.key];
    if (next !== undefined) claims[f.key] = next;
  }
  c.claims = claims;
  c.issueDate = input.issueDate || c.issueDate;
  c.expiresAt = input.expiresAt;

  /* The artwork is drawn from the record's own render-data snapshot, so a correction that never
     reached it would leave the image contradicting the register. */
  const data: Record<string, unknown> = { ...renderData(c) };
  for (const [key, value] of Object.entries(claims)) data[key] = value;
  data.issue_date = fmtDate(c.issueDate, lang);
  data.expiry_date = c.expiresAt ? fmtDate(c.expiresAt, lang) : '';
  c.renderDataSnapshot = data;

  const revision = c.revision + 1;
  c.revision = revision;
  appendEvent(db, c, {
    type: 'corrected',
    command: 'correctNonIdentityClaim',
    actor,
    actorType: 'admin',
    at,
    reason,
    correlationId,
    meta: { changes, revision },
  });
  appendEvent(db, c, {
    type: 'rendered',
    command: 'correctNonIdentityClaim',
    actor: 'Render servisi',
    at,
    correlationId,
    meta: { manifestHash: c.renderManifestHash, revision },
  });
}

// ------------------------------------------------------------------------ atomic revoke + reissue

/**
 * `revokeAndReissueCredential` — `m03-state.js:283`.
 *
 * Rule 8: one transaction. The old record is revoked, the new one is issued, and the two are
 * linked in BOTH directions before anything is observable; a half-finished pair — an old record
 * revoked with no successor, or a successor with no ancestor — is the failure this exists to
 * prevent. The demo runs it inside a single `update()`, so the screen never sees an intermediate
 * database.
 *
 * API GAP: credential.revoke_and_reissue — the same single transaction server-side, with the new
 * public code minted there and an idempotency key so a double click cannot produce two records.
 */
export function reissueInDraft(db: DemoDatabase, oldCode: string, input: IssueInput, reason: string): void {
  const old = db.credentials.find((x) => x.publicCode === oldCode);
  if (!old || !canTransition(old.state, 'revoked')) return;
  revokeInDraft(db, oldCode, reason, input.actor);
  issueInDraft(db, input);
  const fresh = db.credentials.find((x) => x.publicCode === input.publicCode);
  if (!fresh) return;
  const at = prototypeNow();
  const correlationId = newCorrelationId();
  old.supersededByCode = fresh.publicCode;
  fresh.supersedesCode = old.publicCode;
  appendEvent(db, old, {
    type: 'reissued',
    command: 'revokeAndReissueCredential',
    actor: input.actor,
    actorType: 'admin',
    at,
    reason,
    correlationId,
    meta: { newCode: fresh.publicCode },
  });
  appendEvent(db, fresh, {
    type: 'superseded',
    command: 'revokeAndReissueCredential',
    actor: input.actor,
    actorType: 'admin',
    at,
    correlationId,
    meta: { supersedes: old.publicCode },
  });
}

// --------------------------------------------------------------------------- clipboard and tabs

/** `copyText` — `m01-utils.js:227`: the toast fires first, the write is attempted after. */
export function copyText(text: string, toast: (message: string) => void, message = 'Kopyalandı'): void {
  if (message) toast(message);
  const fallback = (): boolean => {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    area.remove();
    return ok;
  };
  const failed = (): void => {
    if (!fallback() && message) toast('Panoya yazılamadı — metni elle kopyalayın');
  };
  try {
    const write = navigator.clipboard?.writeText(text);
    if (write) void write.catch(failed);
    else failed();
  } catch {
    failed();
  }
}

/** `openNewTab` — `m01-utils.js:288`. The opener reference is dropped, as upstream. */
export function openNewTab(url: string): Window | null {
  let w: Window | null = null;
  try {
    w = window.open(url, '_blank');
  } catch {
    w = null;
  }
  if (w) {
    try {
      w.opener = null;
    } catch {
      /* a cross-origin tab refuses the assignment; `rel=noopener` covers the same ground */
    }
  }
  return w;
}

// -------------------------------------------------------------------------- raster output

/**
 * The reference rasterises through its own canvas engine, which is not ported; this draws the
 * SAME SVG the screen shows into a canvas instead, so the PNG cannot disagree with what the
 * reviewer saw. The two font faces are inlined as data URLs first: an `<img>` loading an SVG
 * cannot reach the page's own `@font-face` rules, and without them every glyph would be rendered
 * in a fallback face — a different certificate, not a smaller one.
 *
 * API GAP: credential.render_png — a real download is the artifact the render manifest recorded,
 * produced server-side and content-addressed, not a picture this tab drew (rule 10).
 */
let fontCss: Promise<string> | null = null;

async function inlineFontCss(): Promise<string> {
  if (fontCss === null)
    fontCss = (async () => {
      const faces = [
        { weight: 400, url: publicAssetUrl('/v07/fonts/Poppins-Regular.ttf') },
        { weight: 700, url: publicAssetUrl('/v07/fonts/Poppins-Bold.ttf') },
      ];
      const rules = await Promise.all(
        faces.map(async (f) => {
          /* The bundled font file of this very build, read as a static asset and inlined so the
             print/export surface does not depend on a second request. No identity is attached. */
          const res = await fetch(f.url, { credentials: 'omit', cache: 'force-cache' });
          const bytes = new Uint8Array(await res.arrayBuffer());
          let binary = '';
          for (const b of bytes) binary += String.fromCharCode(b);
          return `@font-face{font-family:"Poppins";font-style:normal;font-weight:${f.weight};src:url(data:font/ttf;base64,${btoa(binary)}) format("truetype")}`;
        }),
      );
      return rules.join('');
    })().catch(() => '');
  return fontCss;
}

async function credentialImage(c: DemoCredential): Promise<HTMLImageElement> {
  /* The stored artifact when the record has one (rule 10), and the drawn design when it does not —
     the same choice the public page makes, so a PNG, a print and a share card off the admin screen
     are the bytes the manifest was hashed over rather than a second drawing of them. */
  const svg = credentialArtwork(c, { responsive: false });
  const css = await inlineFontCss();
  const head = svg.indexOf('>') + 1;
  const withFonts = `${svg.slice(0, head)}<style>${css}</style>${svg.slice(head)}`;
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('SVG rasterlanamadı'));
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(withFonts)}`;
  });
  return image;
}

export async function credentialPng(c: DemoCredential, scale = 1): Promise<string> {
  const image = await credentialImage(c);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(canvasW(c.release.canvas) * scale);
  canvas.height = Math.round(canvasH(c.release.canvas) * scale);
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Canvas bağlamı yok');
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

export async function downloadCredentialPng(
  c: DemoCredential,
  toast: (message: string) => void,
): Promise<void> {
  try {
    const data = await credentialPng(c, 1);
    const a = document.createElement('a');
    a.href = data;
    a.download = `${c.publicCode}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    recordTelemetry(c.id, 'downloaded', { format: 'png' });
    toast('PNG hazırlandı');
  } catch (error) {
    toast(`PNG üretilemedi: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * `printCredential` — `m05-linkedin.js:143`. The page size follows the canvas, as upstream.
 *
 * One deliberate departure: upstream writes a `downloaded` row into the APPEND-ONLY LEDGER here,
 * while the PNG download next to it only records usage. A print is a reader action, the screen's
 * own warning says as much, and a legal ledger that collects them stops being usable as evidence
 * — so this records usage for both.
 */
export async function printCredential(c: DemoCredential, toast: (message: string) => void): Promise<void> {
  const w = canvasW(c.release.canvas);
  const h = canvasH(c.release.canvas);
  const pageSize = Math.abs(w - h) < 4 ? '200mm 200mm' : w >= h ? '297mm 210mm' : '210mm 297mm';
  let png: string;
  try {
    png = await credentialPng(c, 2);
  } catch {
    toast('Yazdırma görseli üretilemedi');
    return;
  }
  recordTelemetry(c.id, 'printed', { format: 'print' });
  const tab = openNewTab('about:blank');
  if (tab === null) {
    toast('Tarayıcı yazdırma penceresini engelledi');
    return;
  }
  const doc = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${credTitle(c)}</title><style>@page{size:${pageSize};margin:0}html,body{margin:0;height:100%}body{display:flex;align-items:center;justify-content:center;background:#fff}img{max-width:100%;max-height:100vh;display:block}@media screen{body{background:#F5F2EF;padding:24px;box-sizing:border-box}img{box-shadow:0 8px 30px rgba(0,0,0,.15)}}</style></head><body><img src="${png}" alt=""></body></html>`;
  tab.document.open();
  tab.document.write(doc);
  tab.document.close();
  /* The written document is already parsed by the time `close()` returns, so waiting on `load`
     would be waiting for an event that has fired; the delay is for the image decode. */
  window.setTimeout(() => tab.print(), 250);
}

/** The state sentence and its colour, keyed by the DERIVED state — `m05-linkedin.js:131`. */
function ogStateLine(c: DemoCredential): { text: string; color: string } {
  switch (credentialViewState(c)) {
    case 'active':
      return { text: `Doğrulanmış · ${fmtDate(c.issueDate)}`, color: '#1E8A4C' };
    case 'expired':
      return { text: `Süresi doldu · ${fmtDate(c.expiresAt)}`, color: '#B4720A' };
    case 'revoked':
      return { text: 'İptal edildi', color: '#000' };
    case 'superseded':
      return { text: 'İptal edildi · yerine yeni kayıt', color: '#000' };
    case 'pending_acceptance':
      return { text: 'Onay bekliyor', color: '#000' };
    case 'pending_admin_publish':
      return { text: 'Yayın onayı bekliyor', color: '#000' };
    default:
      return { text: 'Taslak', color: '#000' };
  }
}

/**
 * `buildOgImage` — `m05-linkedin.js:121`, 1200×630, with the reference's own composition: the
 * artwork on a white panel to the left, the wordmark, the type sentence, the title, the holder,
 * the state line and the public URL to the right.
 *
 * API GAP: the public route has to render this card SERVER-SIDE into the first HTML response —
 * a link unfurler never runs the page's JavaScript, so an OG image a script drew afterwards is an
 * image no social card will ever show.
 */
export async function buildOgImage(c: DemoCredential, verified: string): Promise<string> {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d');
  if (g === null) throw new Error('Canvas bağlamı yok');

  const wrapText = (
    text: string,
    x: number,
    y: number,
    maxW: number,
    lineHeight: number,
    font: string,
    color: string,
  ): number => {
    g.font = font;
    g.fillStyle = color;
    let line = '';
    let yy = y;
    for (const word of text.split(' ')) {
      const attempt = line ? `${line} ${word}` : word;
      if (g.measureText(attempt).width > maxW && line) {
        g.fillText(line, x, yy);
        line = word;
        yy += lineHeight;
      } else line = attempt;
    }
    if (line) g.fillText(line, x, yy);
    return yy + lineHeight;
  };

  g.fillStyle = '#F5F2EF';
  g.fillRect(0, 0, W, H);
  g.fillStyle = '#FFFFFF';
  g.fillRect(0, 0, 520, H);

  const image = await credentialImage(c);
  const iw = canvasW(c.release.canvas);
  const ih = canvasH(c.release.canvas);
  const fit = Math.min(440 / iw, 520 / ih);
  g.drawImage(image, (520 - iw * fit) / 2, (H - ih * fit) / 2, iw * fit, ih * fit);

  g.fillStyle = '#CF051E';
  g.fillRect(580, 60, 26, 26);
  g.fillStyle = '#F8BA3F';
  g.fillRect(600, 80, 10, 10);
  g.fillStyle = '#000';
  g.font = '600 24px Poppins, sans-serif';
  g.textBaseline = 'alphabetic';
  g.fillText('Happy Place to Work', 622, 82);
  g.fillStyle = '#7B756F';
  g.font = '700 14px Poppins, sans-serif';
  g.fillText(verified, 580, 150);

  let y = wrapText(credTitle(c), 580, 200, 560, 46, '500 38px Poppins, sans-serif', '#000');
  y = wrapText(displayName(c), 580, y + 4, 560, 34, '600 28px Poppins, sans-serif', '#000');
  const state = ogStateLine(c);
  g.fillStyle = state.color;
  g.font = '600 20px Poppins, sans-serif';
  g.fillText(state.text, 580, y + 6);

  g.fillStyle = '#8B837C';
  const urlText = publicUrl(c).replace(/^https?:\/\//, '');
  let size = 18;
  g.font = `400 ${size}px Poppins, sans-serif`;
  while (g.measureText(urlText).width > 560 && size > 11) {
    size -= 1;
    g.font = `400 ${size}px Poppins, sans-serif`;
  }
  g.fillText(urlText, 580, H - 50);
  g.fillStyle = '#CF051E';
  g.fillRect(580, H - 30, 560, 4);
  g.fillStyle = '#F8BA3F';
  g.fillRect(1130, H - 36, 14, 14);
  return canvas.toDataURL('image/png');
}

// ------------------------------------------------------------------- LinkedIn helper (`m05:6`)

/**
 * The Turkish half of the reference's `LI` dictionary. The detail screen opens the helper with
 * `lang: 'tr'` (`m08b-admin2.js:258`); the English half belongs to the public and claim surfaces,
 * which open it in the visitor's own language.
 *
 * Rules 14 and 15 are the whole point of this copy: LinkedIn does not pre-fill third-party
 * certification fields, so the helper copies values for the recipient to paste, and nothing here
 * claims a one-click automatic add.
 */
export const LI_TR = {
  title: 'LinkedIn profilinize ekleyin',
  eyebrow: 'LinkedIn · Lisanslar ve Sertifikalar',
  intro:
    "LinkedIn, sertifika alanlarını üçüncü taraflar için otomatik doldurmaz. Form yeni sekmede açıldı; aşağıdaki alanları sırayla kopyalayıp LinkedIn formuna yapıştırın ve LinkedIn tarafında Kaydet'e basın.",
  blocked: 'Tarayıcı yeni sekmeyi engelledi. LinkedIn formunu aşağıdaki düğmeyle açabilirsiniz.',
  reopen: 'LinkedIn formunu yeniden aç ↗',
  open: 'LinkedIn formunu aç ↗',
  progress: (a: number, b: number): string => `${a}/${b} alan kopyalandı`,
  copy: 'Kopyala',
  copied: 'Kopyalandı ✓',
  copyNext: 'Kopyala ve sonraki alana geç',
  fields: {
    name: 'Sertifika adı',
    org: 'Veren kuruluş',
    im: 'Veriliş ayı',
    iy: 'Veriliş yılı',
    em: 'Bitiş ayı',
    ey: 'Bitiş yılı',
    id: 'Credential ID',
    url: 'Credential URL',
  } as Record<string, string>,
  noExpiry:
    "Bu kaydın bitiş tarihi yok: LinkedIn formunda «Bu credential'ın süresi dolmaz» seçeneğini işaretleyin.",
  expiredWarn: "Bu credential'ın süresi dolmuştur. LinkedIn'de bitiş tarihini doğru biçimde belirtin.",
  all: 'Tüm bilgileri kopyala',
  allCopied: 'Tüm bilgiler kopyalandı',
  restart: 'Baştan başla',
  saved: 'Kaydettim',
  close: 'Kapat',
  savedToast: "Teşekkürler — LinkedIn'de kaydettiğinizi not ettik.",
  /* A function, not a sentence: the organisation is a per-template setting, and a certificate
     issued on a customer's behalf must not tell anyone to type the issuer's name into LinkedIn. */
  issuerWarn: (organizationName: string) =>
    `Veren kuruluş alanına «${organizationName}» yazın ve açılan önerilerden resmî LinkedIn Page'i seçin. Yalnız metni yazıp Page'i seçmezseniz kurum logosu ve Page bağlantısı görünmeyebilir.`,
  issuerUnreadable:
    'Bu kaydın veren kuruluş bilgisi okunamadı; LinkedIn profiline ekleme bu kayıt için kullanılamıyor. Sertifikanın doğrulama sayfası ve indirme bundan etkilenmez.',
  issuerUnknown: 'Okunamadı',
  openPage: "Resmî LinkedIn Page'i aç ↗",
  copyIssuer: 'Kuruluş adını kopyala',
  mockPreview: 'Temsili LinkedIn önizlemesi — gerçek Page logosu yüklenmedi.',
  previewTitle: 'LinkedIn profilinde nasıl görünür?',
  previewIssued: 'Verildi',
  previewExp: 'Bitiş',
  previewNoExp: 'Süresiz',
  previewShow: "Credential'ı göster",
  previewNote:
    "LinkedIn'in Lisanslar ve Sertifikalar bölümünde rozet veya sertifika görselinin kendisi yerine veren kuruluşun LinkedIn Page logosu gösterilir. «Credential'ı göster» bağlantısı tıklandığında kişiye özel HPW doğrulama sayfası açılır.",
  allText: {
    name: 'Sertifika adı',
    org: 'Veren kuruluş',
    im: 'Veriliş ayı',
    iy: 'Veriliş yılı',
    em: 'Bitiş ayı',
    ey: 'Bitiş yılı',
    id: 'Credential ID',
    url: 'Credential URL',
    noexp: "Bitiş: süresiz (LinkedIn'de «süresi dolmaz» seçeneği)",
  } as Record<string, string>,
};

export interface LinkedInField {
  key: string;
  label: string;
  value: string;
}

/**
 * `linkedInFields` — `m05-linkedin.js:27`. Month and year are separate, as the form is.
 *
 * `organizationName` is the ALREADY RESOLVED «Veren kuruluş» — the template's own organisation
 * when it set one, the issuer otherwise — because the helper and the Add-to-Profile link have to
 * name the same organisation and there is one resolver for that
 * (`credentialIssuingOrganization`). It used to prefer the release's issuer snapshot over the
 * argument, which would quietly put the issuer back into a certificate issued for somebody else.
 */
export function linkedInFields(c: DemoCredential, organizationName: string): LinkedInField[] {
  const f: LinkedInField[] = [
    { key: 'name', label: LI_TR.fields.name ?? '', value: credTitle(c) },
    { key: 'org', label: LI_TR.fields.org ?? '', value: organizationName },
    { key: 'im', label: LI_TR.fields.im ?? '', value: fmtDate(c.issueDate, 'tr', 'monthName') },
    { key: 'iy', label: LI_TR.fields.iy ?? '', value: fmtDate(c.issueDate, 'tr', 'year') },
  ];
  if (c.expiresAt)
    f.push(
      { key: 'em', label: LI_TR.fields.em ?? '', value: fmtDate(c.expiresAt, 'tr', 'monthName') },
      { key: 'ey', label: LI_TR.fields.ey ?? '', value: fmtDate(c.expiresAt, 'tr', 'year') },
    );
  f.push(
    { key: 'id', label: LI_TR.fields.id ?? '', value: c.publicCode },
    { key: 'url', label: LI_TR.fields.url ?? '', value: publicUrl(c) },
  );
  return f;
}

export function linkedInAllText(c: DemoCredential, organizationName: string): string {
  const lines = linkedInFields(c, organizationName).map(
    (f) => `${LI_TR.allText[f.key] ?? f.label}: ${f.value}`,
  );
  if (!c.expiresAt) lines.splice(4, 0, LI_TR.allText.noexp ?? '');
  return lines.join('\n');
}
